import {AfterViewInit, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {BaseView} from '../../../../core/view/base.view';
import {
    AbstractControl,
    FormGroup,
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators
} from '@angular/forms';
import {
    FormDefinitionFormFindOneByIdService
} from '../../../../form/definition/form/impl/service/formDefinitionFormFindOneById.service';
import {Observable, Subject, Subscription} from 'rxjs';
import {ExamFormFindOneByIdService} from '../../service/examFormFindOneById.service';
import {ExamFormSaveService} from '../../service/examFormSave.service';
import {ExamFormSavePayload} from '../../service/payload/examFormSave.payload';
import {FormDefinitionFormModel} from '../../../../form/definition/form/api/entity/formDefinitionForm.model';
import {ExamFormValuePayload} from '../../service/payload/examFormValue.payload';
import {PopoverController} from '@ionic/angular';
import {MomentAdapter} from '../../../../../shared/utils/moment.utils';
import {FormDefinitionFieldModel} from '../../../../form/definition/field/api/entity/formDefinitionField.model';
import {
    CommentDialogInput,
    CommentDialogResult,
    CommentMenuComponent
} from '../../../../core/component/comment/comment-menu/comment-menu.component';
import {ExamFormModel} from '../../../api/model/exam-form.model';
import {FormFormSaveComponent} from '../../../../form/form/api/component/form-form-save/form-form-save.component';
import {
    FormDefinitionFormSummaryModel
} from '../../../../form/definition/form/api/entity/formDefinitionFormSummary.model';
import {
    ConsultationSummaryModel
} from '../../../../medical/consultation/consultation/api/model/consultationSummary.model';
import {IDialogComponent} from '../../../../common/dialog/iDialog.component';
import {DialogWithInput} from '../../../../common/dialog/dialogWithInput';
import {DialogController} from '../../../../common/dialog/dialogController';
import {
    CreateUpdateDefaultComponent
} from '../../../../core/component/create-update-default/create-update-default.component';
import {ExamFormPaginatedService} from '../../service/examFormPaginated.service';
import {FormValueModel} from '../../../../form/form/value/api/entity/formValue.model';
import {UrlParameterBuilder} from "../../../../core/http/UrlParameterBuilder";
import {
    ConsultationTranscriptionHandler
} from "../../../../medical/consultation/consultation/api/service/transcription/consultationTranscriptionHandler";
import {en} from "@fullcalendar/core/internal-common";
import {PartnerFindOneByIdService} from "../../../../partner/common/api/service/partnerFindOneById.service";
import {
    PartnerSummaryFindOneByIdService
} from "../../../../partner/common/api/service/partnerSummaryFindOneById.service";
import {PartnerSummaryModel} from "../../../../partner/common/api/model/partnerSummary.model";
import {Operator} from "../../../../../shared/utils/url.builder";


export interface ExamFormSaveDialogInput {
    id?: number;
    previousExam?: ExamFormModel;

    // The 3 following fields are for creation purpose only.
    consultationSummary?: ConsultationSummaryModel;
    partnerId?: number;
    // Mandatory in any edition scenario
    formDefinitionId?: number;
    startAudioTranscription?: boolean;
}

export interface ExamFormSaveDialogResult {
    created: boolean;
    entity: ExamFormModel;
}

@Component({
    templateUrl: './exam-form-save.page.html',
    providers: [FormDefinitionFormFindOneByIdService,
        ExamFormSaveService,
        ExamFormPaginatedService,
        ExamFormFindOneByIdService,
        ConsultationTranscriptionHandler,
        PartnerSummaryFindOneByIdService
    ],
})
export class ExamFormSavePage  extends BaseView implements
    IDialogComponent<ExamFormSaveDialogInput, ExamFormSaveDialogResult>,
    OnInit, OnDestroy, AfterViewInit {

    @ViewChild('createUpdateDefaultComponent') createUpdateDefaultComponent: CreateUpdateDefaultComponent<any, any, any>;
    @ViewChild('formFormSaveComponent') formFormSaveComponent: FormFormSaveComponent;
    dialog: DialogWithInput<ExamFormSaveDialogInput, ExamFormSaveDialogResult>;
    partner: PartnerSummaryModel;
    formGroup: FormGroup;
    loadingFormModel$: Observable<boolean> = this.formDefinitionFormFindOneByIdService.loading$;
    fieldFormGroup: UntypedFormGroup;
    onDestroy = new Subject<void>();
    newFields: FormDefinitionFieldModel[] = [];
    entity: ExamFormModel;
    subscriptions: Subscription[] = [];
    examForm: ExamFormModel;
    allCardsExpanded = true;
    medicalInstrumentSetParameters = new UrlParameterBuilder()
        .addNullParameter('useDate')
        .addMomentParameter('validationDate', MomentAdapter.current()
            .setMinute(0)
            .setHour(0)
            .get(), Operator.GREATER_OR_EQUAL)
        .parameters;
    closeDialogOnSave = true;

    constructor(
        private formBuilder: UntypedFormBuilder,
        private formDefinitionFormFindOneByIdService: FormDefinitionFormFindOneByIdService,
        private dialogController: DialogController,
        public service: ExamFormSaveService,
        public findOneService: ExamFormFindOneByIdService,
        private partnerFindOneService: PartnerSummaryFindOneByIdService,
        private transcriptionHandler: ConsultationTranscriptionHandler
    ) {
        super();

        this.subscriptions.push(this.findOneService.entity$.subscribe(value => {
            this.formDefinitionChanged(value?.form?.formDefinition?.summary);
            this.partner = value.form.summary.partner;
        }));

        this.subscriptions.push(this.formDefinitionFormFindOneByIdService.entity$.subscribe(value => {
            if (value) {
                // If formDefinition is loaded -> we are in creation mode;
                this.formDefinitionChanged(value?.summary);
                this.examForm = {
                    form: {
                        values: [],
                        summary: undefined,
                        formDefinition: value,
                        customValues: [],
                    },
                    summary: undefined,
                    consultation: undefined,
                };
                if (this.dialog.input.previousExam) {
                    this.fillWithPreviousExam();
                }
            }
        }));

        this.formGroup = this.formBuilder.group({
            partnerId : this.formBuilder.control(undefined, Validators.required),
            consultationId : this.formBuilder.control(undefined),
            comment: this.formBuilder.control(''),
            summary: this.formBuilder.group({
                id: this.formBuilder.control(undefined)
            }),
            medicalInstrumentSetIds: this.formBuilder.control([]),
        });
        this.fieldFormGroup = this.formBuilder.group({
            uniqueId: MomentAdapter.current().time(),
            id: this.formBuilder.control(null),
            data: this.formBuilder.control(null),
        });
    }
    ngOnInit() {
        if (!this.dialog.input.id) {
            this.formDefinitionFormFindOneByIdService.loadEntity(this.dialog.input.formDefinitionId);
            if (this.dialog.input.consultationSummary) {
                this.formGroup.patchValue({
                    consultationId: this.dialog.input.consultationSummary.id,
                    partnerId: this.dialog.input.consultationSummary.partner.id,
                });
                this.partner = this.dialog.input.consultationSummary.partner;
            } else {
                this.formGroup.patchValue({
                    partnerId: this.dialog.input.partnerId
                });
                this.partnerFindOneService.loadEntity(this.dialog.input.partnerId)
                    .then(value => this.partner = value);
            }
        }
    }

    ngAfterViewInit() {
        console.log(['ngAfterViewInit exam', this.dialog.input]);
        if (this.dialog.input.startAudioTranscription) {
            this.showComments(true);
        }
    }


    ngOnDestroy() {
        this.onDestroy.next();
        this.onDestroy.complete();
        this.subscriptions.forEach(value => value?.unsubscribe());
    }

    fillWithPreviousExam() {
        this.examForm = {
           form: {
               values: this.dialog.input.previousExam.form.values.map(
                   value1 => {
                       return  {
                           value: value1.value,
                           document: value1.document,
                           fieldDefinition: value1.fieldDefinition,
                           label: value1.label
                       } as FormValueModel;
                   }
               ),
               customValues: this.examForm.form.customValues,
               summary: undefined,
               formDefinition: this.examForm.form.formDefinition,
           },
           summary: undefined,
           consultation: undefined,
       };
        console.log(['fillWithPreviousExam', this.examForm]);
    }

    private formDefinitionChanged(formDefinition: FormDefinitionFormSummaryModel) {
        if (formDefinition) {
           this.allCardsExpanded = formDefinition.name !== 'examen_palpatoire';
        }
    }

    private allFields(): AbstractControl[] {
        const fieldsArrayForm: UntypedFormArray = this.formGroup.get('fields') as UntypedFormArray;
        const groupsArrayForm: UntypedFormArray = this.formGroup.get('groups') as UntypedFormArray;
        return fieldsArrayForm.controls
            .concat(groupsArrayForm.controls
                .map((group: UntypedFormGroup) => group.get('fields') as UntypedFormArray)
                .map((fieldFormArray: UntypedFormArray) => fieldFormArray.controls)
                .reduce((a, b) => a.concat(b), [])
            );
    }

    entityLoaded(entity: ExamFormModel) {
        this.examForm = entity;
        this.formGroup.patchValue({
            partnerId: entity.form.summary.partner.id,
            consultationId: entity.consultation?.id,
            comment: entity.form.summary.comment,
            summary: {
                id: entity.summary.id
            }
        });
    }

    buildPayload = (): ExamFormSavePayload => {
        const formDefinitionFormModel: FormDefinitionFormModel = this.formGroup.get('formDefinition').value;
        const values = this.allFields().map(abstractControl => {
            let value = abstractControl.get('value').value;
            if (abstractControl.get('measured').value === false) {
                value = undefined;
            }
            return this.mapControlToExamFormValuePayload(abstractControl, value);
        });
        const customValuesRaw: any[] = this.formGroup.value.customValues;
        let customValues: string[] = [];
        if (customValuesRaw && customValuesRaw.length > 0) {
            customValues = customValuesRaw.map(value => value.value);
        }

        return {
            summary: {
                id: this.formGroup.get('summary.id').value
            },
            partnerId : this.formGroup.get('partnerId').value,
            formDefinitionId : formDefinitionFormModel.summary.id,
            values,
            customValues,
            consultationId: this.formGroup.controls.consultationId.value,
            date: this.formGroup.controls.date.value,
            comment: this.formGroup.controls.comment.value,
            medicalInstrumentSetIds: this.formGroup.value.medicalInstrumentSetIds
        };
    }

    private mapControlToExamFormValuePayload(control: AbstractControl, value): ExamFormValuePayload {
        return {
            fieldDefinitionName : control.get('name').value,
            value,
            comment: control.get('comment').value,
        };
    }

    executeSave(closeDialogOnSave: boolean) {
        this.closeDialogOnSave = closeDialogOnSave;
        this.createUpdateDefaultComponent.save();
    }

    entitySaved(entity: ExamFormModel) {
        if (! this.closeDialogOnSave) {
            this.toastControl.presentConfirmation('Modifications enregistrées');
            this.formGroup.patchValue({
                summary: {
                    id: entity.summary.id,
                }
            })
        } else {
            this.dialog.dialog.close({
                created: !!this.formGroup.get('summary.id').value,
                entity
            });
        }
    }

    showComments(startAudioTranscription: boolean) {
        console.log('Show comments');
        let context = 'partnerId=' + this.formGroup.get('partnerId').value;
        if (this.formGroup.get('consultationId').value) {
            context += '&consultationId=' + this.formGroup.get('consultationId').value;
        }
        if (this.dialog.input.formDefinitionId) {
            context += '&formDefinitionId=' + this.dialog.input.formDefinitionId;
        } else if (this.dialog.input.id && this.examForm.summary.form.formDefinition.id) {
            context += '&formDefinitionId=' + this.examForm.summary.form.formDefinition.id;
        }

        this.transcriptionHandler.setup({
            partnerId: this.formGroup.get('partnerId').value,
            consultationId: this.formGroup.get('consultationId').value,
        });
        const input: CommentDialogInput = {
            comment: this.formGroup.get('comment').value,
            audioTranscriptionConfig: {
                startAutomatically: startAudioTranscription,
                audioContentValue: 'AUDIO_CONTENT_MEDICAL_CONSULTATION_EXAM',
                context,
                enableConversationOption: false,
                showPreview :true,
                handler: this.transcriptionHandler
            }
        };
        const dialog = this.dialogController.open(CommentMenuComponent,
            input,
            {
                title: 'Commentaire'
            });
        dialog.afterClosed().toPromise()
            .then((value: CommentDialogResult) => {
                console.log(['comment dialog closed', value]);
                if (value) {
                    this.formGroup.get('comment').setValue(value.comment);
                }
            });
    }
}
